// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { StepperContext } from "@monsma/App/Widgets/CheckoutStepper/StepperContext";
import { DOMNode, domToReact, HTMLReactParserOptions } from "html-react-parser";
import React, { ChangeEvent, FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setFulfillmentMethod,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const billTo = getCurrentBillToState(state).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;

    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        isLoading: cartState.isLoading,
        cart: cartState.value,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(cartState.value),
        hasProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
        canUpdateShipToAddress:
            billTo?.isGuest ||
            shipTo?.oneTimeAddress ||
            customerSettings.allowBillToAddressEdit ||
            customerSettings.allowShipToAddressEdit,
        updateCartInsteadOfContext: cartState.value?.isAwaitingApproval || !!cartId,
        // custom
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isCheckoutPage: isItCheckoutPage(getCurrentPage(state).type),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// xng-change: styles, wrapper and title and hide buttons until step 4
export interface XStyle {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    itemsCount?: TypographyPresentationProps;
    editCart?: LinkPresentationProps;
}

export interface CheckoutReviewAndSubmitCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    hasInsufficientInventoryGridItem?: GridItemProps;
    availabilityErrorMessageText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    invalidEditableAddressErrorMessageGridItem?: GridItemProps;
    invalidEditableAddressErrorMessageText?: TypographyPresentationProps;
    invalidAddressErrorMessageGridItem?: GridItemProps;
    invalidAddressErrorMessageText?: TypographyPresentationProps;
    invalidPriceErrorMessageGridItem?: GridItemProps;
    invalidPriceErrorMessageText?: TypographyPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    defaultFulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    defaultFulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    // xng extras
    xStyle?: XStyle;
}

export const cartTotalStyles: CheckoutReviewAndSubmitCartTotalStyles = {
    container: { gap: 10 },
    cartTotalGridItem: { width: 12 },
    buttonsGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
            flex-direction: column;
        `,
    },
    hasInsufficientInventoryGridItem: { width: 12 },
    availabilityErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    placeOrderErrorMessageGridItem: { width: 12 },
    placeOrderErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidEditableAddressErrorMessageGridItem: { width: 12 },
    invalidEditableAddressErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidAddressErrorMessageGridItem: { width: 12 },
    invalidAddressErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidPriceErrorMessageGridItem: { width: 12 },

    invalidPriceErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    placeOrderButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    defaultFulfillmentMethodRadioGroup: {
        css: css`
            display: block;
            ${RadioStyle} {
                display: inline-block;
                margin-right: 1em;
                & + ${RadioStyle} {
                    margin-top: 0;
                }
            }
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutReviewAndSubmitCartTotal: FC<Props> = ({
    showPlaceOrderButton,
    cart,
    isLoading,
    shippingPromotions,
    discountTotal,
    orderPromotions,
    placeOrderErrorMessage,
    updatePickUpWarehouse,
    setFulfillmentMethod,
    showSubmitForApprovalOrder,
    canUpdateShipToAddress,
    hasProductsWithInvalidPrice,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
    // custom
    cartUrl,
    id,
    isCheckoutPage,
}) => {
    const { currentStep } = useContext(StepperContext);

    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const openWarehouseSelectionModal = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const toasterContext = React.useContext(ToasterContext);
    const handleWarehouseSelected = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }

        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        setIsFindLocationOpen(false);
    };

    const [isFulfillmentMethodOpen, setIsFulfillmentMethodOpen] = React.useState(false);
    const openDeliveryMethodPopup = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFulfillmentMethodOpen(true);
    };

    const modalCloseHandler = () => {
        setIsFulfillmentMethodOpen(false);
    };

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp",
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
        }
    };

    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            if (!("name" in node && "children" in node)) {
                return;
            }

            const { name, children, attribs = { style: "" } } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            if (name === "a") {
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        if (otherAttribs[key] === "vm.openWarehouseSelectionModal()") {
                            onClickHandler = openWarehouseSelectionModal;
                        }
                        if (otherAttribs[key] === "vm.openDeliveryMethodPopup()") {
                            onClickHandler = openDeliveryMethodPopup;
                        }
                    }
                }
                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                            onClickHandler && onClickHandler(event)
                        }
                    >
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Link>
                );
            }
        },
    };

    // xng-change
    let cartItemsTotal = cart?.cartLines?.length || 0;
    if (isCheckoutPage) {
        cartItemsTotal =
            cart?.cartLines?.filter(cartLine => cartLine.properties?.includeInCheckout === "True").length || 0;
    }

    return (
        <>
            <Typography {...styles.xStyle?.title}>{translate("Order Summary")}</Typography>
            <StyledWrapper {...styles.xStyle?.wrapper}>
                <Typography {...styles.xStyle?.itemsCount}>
                    {cartItemsTotal} {translate("Items")}
                </Typography>
                <Link {...styles.xStyle?.editCart} href={cartUrl}>
                    Edit Cart
                </Link>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.cartTotalGridItem}>
                        <CartTotalDisplay
                            cart={cart}
                            isLoading={isLoading}
                            shippingPromotions={shippingPromotions}
                            discountTotal={discountTotal}
                            orderPromotions={orderPromotions}
                            // custom
                            showTaxAndShipping={false}
                            {...({
                                showCostTaxMessage: true,
                                injectZone: currentStep === 4 ? <Zone zoneName="Promotion" contentId={id} /> : null,
                            } as any)}
                        />
                    </GridItem>
                    {currentStep === 4 && cart && showPlaceOrderButton && (
                        <GridItem {...styles.buttonsGridItem}>
                            <PlaceOrderButton styles={styles.placeOrderButton} />
                        </GridItem>
                    )}
                    {currentStep === 4 && cart && showSubmitForApprovalOrder && (
                        <GridItem {...styles.buttonsGridItem}>
                            <SubmitForApprovalButton extendedStyles={styles.submitForApprovalButton} />
                        </GridItem>
                    )}
                    {cart && cart.hasInsufficientInventory === true && (
                        <GridItem {...styles.hasInsufficientInventoryGridItem}>
                            <Typography
                                {...styles.availabilityErrorMessageText}
                                data-test-selector="checkoutReviewAndSubmit_availabilityErrorMessage"
                            >
                                {siteMessageWithCustomParserOptions(
                                    "ReviewAndPay_NotEnoughInventoryForPickup",
                                    parserOptions,
                                )}
                            </Typography>
                            <FindLocationModal
                                modalIsOpen={isFindLocationOpen}
                                onWarehouseSelected={handleWarehouseSelected}
                                onModalClose={handleFindLocationModalClose}
                                forCart
                                extendedStyles={styles.findLocationModal}
                            />
                            <Modal
                                size={500}
                                headline={translate("Fulfillment Method")}
                                isOpen={isFulfillmentMethodOpen}
                                handleClose={modalCloseHandler}
                            >
                                <RadioGroup
                                    value={cart.fulfillmentMethod}
                                    onChangeHandler={fulfillmentMethodChangeHandler}
                                    {...styles.defaultFulfillmentMethodRadioGroup}
                                >
                                    <Radio value={FulfillmentMethod.Ship} {...styles.defaultFulfillmentMethodRadio}>
                                        {translate("Ship")}
                                    </Radio>
                                    <Radio value={FulfillmentMethod.PickUp} {...styles.defaultFulfillmentMethodRadio}>
                                        {translate("Pick Up")}
                                    </Radio>
                                </RadioGroup>
                            </Modal>
                        </GridItem>
                    )}
                    {placeOrderErrorMessage && (
                        <GridItem {...styles.placeOrderErrorMessageGridItem}>
                            <Typography
                                {...styles.placeOrderErrorMessageText}
                                data-test-selector="checkoutReviewAndSubmit_placeOrderErrorMessage"
                            >
                                {placeOrderErrorMessage}
                            </Typography>
                        </GridItem>
                    )}
                    {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                        <GridItem {...styles.invalidEditableAddressErrorMessageGridItem}>
                            <Typography {...styles.invalidEditableAddressErrorMessageText}>
                                {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                        <GridItem {...styles.invalidAddressErrorMessageGridItem}>
                            <Typography {...styles.invalidAddressErrorMessageText}>
                                {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}
                            </Typography>
                        </GridItem>
                    )}
                    {hasProductsWithInvalidPrice && (
                        <GridItem {...styles.invalidPriceErrorMessageGridItem}>
                            <Typography {...styles.invalidPriceErrorMessageText}>
                                {siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCartTotal),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
